import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, X as XIcon } from "lucide-react";
import { trackPurchase } from "@/lib/metaPixel";
import { trackPayment } from "@/lib/gtm";

// Paystack returns here after the R1 card-capture transaction. We verify the
// transaction, schedule the deferred subscription server-side (handled by
// GET /invite-trial/verify), and bounce the user to their role portal.
//
// This page is dedicated to the invite trial so the regular /payment/callback
// behaviour isn't touched.
export default function InviteTrialCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | failed
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const reference = params.get("reference") || params.get("trxref");
    if (!reference) {
      setState("failed");
      setErrorMsg("Missing payment reference. Please try again from the trial page.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invite-trial/verify/${reference}`);
        if (cancelled) return;
        if (data.status === "success") {
          setState("success");
          trackPurchase({ currency: "ZAR", content_name: "invite_trial" });
          trackPayment({ redirect: data.redirect, reference, plan: "invite_trial", flow: "invite_trial" });
          const fresh = await refresh();
          toast.success("Trial started — your card is saved for the first charge in 90 days");
          setTimeout(() => {
            // Prefer the backend-provided role-based redirect.
            if (data.redirect) {
              navigate(data.redirect);
              return;
            }
            navigate(fresh ? dashboardFor(fresh) : "/");
          }, 1800);
        } else {
          setState("failed");
          setErrorMsg(`Payment ${data.status || "failed"}. Please try again from the trial page.`);
        }
      } catch (e) {
        if (cancelled) return;
        setState("failed");
        setErrorMsg(errMsg(e, "We couldn't verify your card. Please try again."));
      }
    })();
    return () => { cancelled = true; };
  }, [params, navigate, refresh]);

  if (state === "verifying") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-trial-callback-verifying">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-10 text-center text-forest/60 max-w-md w-full">
          Finishing up your 90-day trial… hang tight.
        </div>
      </div>
    );
  }

  if (state === "success") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-trial-callback-success">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 max-w-md w-full text-center shadow-sm fade-in">
          <div className="w-14 h-14 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-sage-dark" />
          </div>
          <h1 className="text-xl font-black text-forest mb-2">You're in — enjoy your 90 days!</h1>
          <p className="text-sm text-forest/70">We've saved your card and scheduled your first R149 payment for the end of your trial. Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-trial-callback-failed">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 max-w-md w-full text-center shadow-sm fade-in">
        <div className="w-14 h-14 rounded-full bg-terracotta-light flex items-center justify-center mx-auto mb-4">
          <XIcon className="w-7 h-7 text-terracotta-dark" />
        </div>
        <h1 className="text-xl font-black text-forest mb-2">We couldn't start your trial</h1>
        <p className="text-sm text-forest/70 mb-6" data-testid="invite-trial-callback-error-message">{errorMsg}</p>
        <button
          onClick={() => navigate("/invite-trial-subscribe")}
          className="rounded-full bg-sage hover:bg-sage-dark text-white px-6 py-2 font-bold"
          data-testid="invite-trial-callback-retry-btn"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
